/**
 * Category Icon Mapping
 *
 * Maps kebab-case icon name strings (stored in database) to Lucide React components.
 *
 * Design Rationale:
 * - Icon names are stored as strings in the database for flexibility and migration safety
 * - This mapping layer translates those strings to actual React components for rendering
 * - Using a centralized map ensures consistency across all category displays
 *
 * Icon Name Convention:
 * - Uses kebab-case format (e.g., "file-text", "bar-chart-3") to match Lucide naming
 * - Must match icon names used in seed data (convex/seed/data/categories.ts)
 *
 * Adding New Icons:
 * 1. Import the Lucide icon component from 'lucide-react'
 * 2. Add mapping entry to ICON_MAP with kebab-case name
 * 3. Update domain layer ALLOWED_ICONS if validation is needed
 *
 * Defensive Programming:
 * - Returns null for unknown icons instead of throwing errors
 * - Calling code must handle null case with fallback icon (typically FolderOpen)
 */

import {
  Award,
  BarChart3,
  BookOpen,
  Briefcase,
  CheckSquare,
  Clipboard,
  Database,
  FileSignature,
  FileText,
  Flag,
  Folder,
  GitBranch,
  Globe,
  Heart,
  Key,
  Layers,
  Lock,
  Mail,
  Map as MapIcon,
  Monitor,
  Package,
  PenTool,
  Shield,
  Star,
  Table2,
  type LucideIcon,
} from "lucide-react";

/**
 * Map of icon name strings to Lucide icon components
 *
 * Note: Icon names use kebab-case to match Lucide's naming convention
 * and to be consistent with database storage format
 */
const ICON_MAP: Record<string, LucideIcon> = {
  "file-text": FileText,
  award: Award,
  "git-branch": GitBranch,
  "bar-chart-3": BarChart3,
  shield: Shield,
  table: Table2,
  "file-signature": FileSignature,
  "check-square": CheckSquare,
  folder: Folder,
  "book-open": BookOpen,
  briefcase: Briefcase,
  clipboard: Clipboard,
  database: Database,
  flag: Flag,
  globe: Globe,
  heart: Heart,
  key: Key,
  layers: Layers,
  lock: Lock,
  mail: Mail,
  map: MapIcon,
  monitor: Monitor,
  package: Package,
  "pen-tool": PenTool,
  star: Star,
};

/**
 * Get the Lucide icon component for a given icon name string.
 *
 * Returns null for unknown icons to enable defensive rendering with fallbacks.
 * Callers should handle null case: `getCategoryLucideIcon(name) ?? FolderOpen`
 *
 * @param name - The icon name in kebab-case (e.g., "file-text", "award")
 * @returns The Lucide icon component or null if not found
 */
export function getCategoryLucideIcon(name: string): LucideIcon | null {
  return ICON_MAP[name] ?? null;
}

/**
 * Get all available icon names for icon picker UI
 *
 * Used by LucideIconPicker component to display available icons
 *
 * @returns Array of icon name strings in kebab-case format
 */
export function getAvailableIconNames(): string[] {
  return Object.keys(ICON_MAP);
}
